import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useStore } from '../lib/store';
import { haptics } from '../lib/utils/haptics';
import { toast } from 'sonner';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { CategoryNav } from '../components/layout/CategoryNav';
import { ProductImageGallery } from '../components/product/ProductImageGallery';
import { ProductInfo } from '../components/product/ProductInfo';
import { BundleDeals } from '../components/product/BundleDeals';
import { AddToCart } from '../components/product/AddToCart';
import { DeliveryInfo } from '../components/product/DeliveryInfo';
import { ProductBenefits } from '../components/product/ProductBenefits';
import { FrequentlyBoughtTogether } from '../components/product/FrequentlyBoughtTogether';
import { ProductTabs } from '../components/product/ProductTabs';
import { CustomersAlsoViewed } from '../components/product/CustomersAlsoViewed';
import { RelatedProducts } from '../components/product/RelatedProducts';
import { PrescriptionRequired } from '../components/product/PrescriptionRequired';
import { PharmacistConsultation } from '../components/product/PharmacistConsultation';
import { StickyBottomActions } from '../components/product/StickyBottomActions';
import { SEO } from '../components/shared/SEO';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { HomeIcon, ChevronRightIcon } from 'lucide-react';
import { ComboComponents } from '../components/product/ComboComponents';
import { PaymentOptions } from '../components/product/PaymentOptions';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PRODUCT_IMAGES } from '../data/productImages';
export function ProductDetailPage() {
  const {
    productId
  } = useParams();
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Mock product data
  const product = {
    id: productId || '1',
    name: 'COSRX Advanced Snail 96 Mucin Power Essence 100ml',
    brand: 'COSRX',
    sku: '135556',
    price: 99.25,
    originalPrice: 106,
    discount: 15,
    rating: 4.8,
    reviewCount: 324,
    inStock: true,
    stockStatus: 'In Stock',
    isGlobal: true,
    images: [PRODUCT_IMAGES[0], PRODUCT_IMAGES[1], PRODUCT_IMAGES[2], PRODUCT_IMAGES[3], PRODUCT_IMAGES[4], PRODUCT_IMAGES[0]],
    tags: ['Dry Skin', 'Dark Circles'],
    prescriptionNeeded: false,
    description: 'COSRX Advanced Snail 96 Mucin Power Essence is a lightweight essence that nourishes, repairs, and revitalizes the skin. Formulated with 96.3% Snail Secretion Filtrate, it helps to improve skin elasticity, soothe redness, and provide deep hydration for a healthy, glowing complexion.',
    loyaltyPoints: 99
  };
  const breadcrumbItems = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Skincare',
    href: '/category/skin-care'
  }, {
    label: 'Essences & Serums',
    href: '/category/essences-serums'
  }, {
    label: product.name
  }];
  // Generate product URL key from product name
  const productUrlKey = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  // Generate keywords from product data
  const productKeywords = [product.brand, product.name, ...product.tags, 'Saudi Arabia', 'online pharmacy', 'authentic products', 'fast delivery'].join(', ');
  // SEO title and description
  const seoTitle = `${product.brand} ${product.name} Price ${product.price} SAR`;
  const seoDescription = `Buy ${product.name} by ${product.brand} at Innova Pharmacy — authentic product with fast delivery across Saudi Arabia and secure checkout in SAR.`;
  return <HelmetProvider data-id="element-7856" data-mp="1reucios5g9tn">
      <SEO title={seoTitle} description={seoDescription} canonical={`/en/${productUrlKey}/`} ogType="product" ogImage={product.images[0]} ogPrice={product.price} productPrice={product.price} productCurrency="SAR" robots="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" twitterCard="summary_large_image" hreflangAr={`/${productUrlKey}/`} hreflangEn={`/en/${productUrlKey}/`} hreflangDefault={`/${productUrlKey}/`} author={product.brand} keywords={productKeywords} data-id="element-7857" data-mp="1rekmx2b0o4oh" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7858" data-mp="3g1nbv320cg3">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7859" data-mp="3umcm1kkld149">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={false} showAnnouncement={true} data-id="element-7860" data-mp="3upgwgzongt3d" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)] pb-32 lg:pb-0" data-id="element-7861" data-mp="3bwwggiu1uigz">
          {/* Mobile Header Overlay */}
          <MobileInternalHeader title={product.name} data-id="element-7862" data-mp="3qmuavgiko4em" />

          {/* Page Hero Breadcrumb - Path Only Mode */}
          <PageHeroBreadcrumb breadcrumbs={breadcrumbItems} title={product.name} description={product.description} showTitle={false} showDescription={false} data-id="element-7863" data-mp="3luaq9zlgw90b" />
          {/* Product Main Section */}
          <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-8 lg:pb-6" data-id="element-7864" data-mp="3iv2f3gqi3q2f">
            {/* Mobile Layout - Reordered */}
            <div className="md:hidden space-y-4" data-id="element-7865" data-mp="3mu1fco1aeynj">
              {/* Product Image Gallery */}
              <div data-id="element-7866" data-mp="3rnmeijem9yob">
                <ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7867" data-mp="3rp72fq6fejd7" />
              </div>
              {/* Product Info - without ComboComponents and PaymentOptions */}
              <div data-id="element-7868" data-mp="38vlal2mzji81">
                <ProductInfo product={product} hideComboComponents={true} hidePaymentOptions={true} data-id="element-7869" data-mp="38v1s9ktfarth" />
              </div>
              {/* Bundle & Save */}
              <div data-id="element-7870" data-mp="t58ilad5l3hx">
                <BundleDeals product={product} data-id="element-7871" data-mp="t4p9mj5hdpg1" />
              </div>
              {/* Frequently Bought Together */}
              <div data-id="element-7872" data-mp="2dm7w0xln7bpv">
                <FrequentlyBoughtTogether data-id="element-7873" data-mp="2dmq3kqdr9cd3" />
              </div>
              {/* Combo Components */}
              <div data-id="element-7874" data-mp="28oojc24lf701">
                <ComboComponents data-id="element-7875" data-mp="28q9bxm7ckfv9" />
              </div>
              {/* Payment Options */}
              <div data-id="element-7876" data-mp="23vdajvg80g5h">
                <PaymentOptions price={product.price} data-id="element-7877" data-mp="1yyd1rcrzkzao" />
              </div>
              {/* Prescription Required */}
              <div data-id="element-7878" data-mp="1uxb3ay5w8r9r">
                <PrescriptionRequired data-id="element-7879" data-mp="1uxt4m97exvoj" />
              </div>
              {/* Pharmacist Consultation */}
              <div data-id="element-7880" data-mp="1pwvr49xclptd">
                <PharmacistConsultation data-id="element-7881" data-mp="1pwdmoq0j85ad" />
              </div>
              {/* Delivery Information */}
              <div data-id="element-7882" data-mp="1kyu5bh8jfce7">
                <DeliveryInfo data-id="element-7883" data-mp="1fzio6749e4w3" />
              </div>
            </div>

            {/* Tablet Layout (md) */}
            <div className="hidden md:grid lg:hidden grid-cols-2 gap-6" data-id="element-7884" data-mp="3fcd0ikf3iiwc">
              <div className="space-y-6" data-id="element-7885" data-mp="3faiojvlsy14e">
                <div className="sticky top-36" data-id="element-7886" data-mp="3ad0c3v8kt3rn">
                  <ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7887" data-mp="3aebky9gvp1pm" />
                  <div className="mt-6" data-id="element-7888" data-mp="2aet6tegnezcq">
                    <BundleDeals product={product} data-id="element-7889" data-mp="2adrpwtcybin3" />
                  </div>
                </div>
              </div>
              <div className="space-y-6" data-id="element-7890" data-mp="20hogd2frshq4">
                <ProductInfo product={product} data-id="element-7891" data-mp="20gwvaljsu2e3" />
                <AddToCart product={product} data-id="element-7892" data-mp="25grm6p3kqrz5" />
                <FrequentlyBoughtTogether data-id="element-7893" data-mp="25ht154npnym5" />
                <PrescriptionRequired data-id="element-7894" data-mp="25eevnnjr39ah" />
                <PharmacistConsultation data-id="element-7895" data-mp="1rhrz3lvpml53" />
                <DeliveryInfo data-id="element-7896" data-mp="1rit87kt60xk6" />
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:grid lg:grid-cols-[400px_1fr_400px] xl:grid-cols-[420px_1fr_420px] 2xl:grid-cols-[440px_1fr_440px] gap-4 xl:gap-6" data-id="element-7897" data-mp="1vko9wqf0dcyh">
              {/* Left Column - Gallery (Sticky) */}
              <div className="order-1" data-id="element-7898" data-mp="3pua4ao2uwelj">
                <div className="sticky top-24" data-id="element-7899" data-mp="1e8hptd7r4tld">
                  <ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7900" data-mp="1e76lncae9jtq" />
                </div>
              </div>
              {/* Middle Column - Product Info + Frequently Bought Together */}
              <div className="order-2 space-y-6" data-id="element-7901" data-mp="1xvfc9hwahhr4">
                <div className="sticky top-24" data-id="element-7902" data-mp="1syowkvoeco68">
                  <ProductInfo product={product} data-id="element-7903" data-mp="1sxvzynj5jf1t" />
                </div>
                <div data-id="element-7904" data-mp="26wvozwaktvyi">
                  <FrequentlyBoughtTogether data-id="element-7905" data-mp="26xex6lafd5zc" />
                </div>
              </div>
              {/* Right Column - Sidebar (Sticky) */}
              <div className="order-3" data-id="element-7906" data-mp="cjnobnzx6q4y">
                <div className="sticky top-24 space-y-4 xl:space-y-6" data-id="element-7907" data-mp="ckozdsgxrcmo">
                  <BundleDeals product={product} data-id="element-7908" data-mp="7lcdb9xw29l5" />
                  <AddToCart product={product} data-id="element-7909" data-mp="7mngp8nf9fbq" />
                  <PrescriptionRequired data-id="element-7910" data-mp="vipzqgyph9cy" />
                  <PharmacistConsultation data-id="element-7911" data-mp="vjhujpkhb326" />
                  <DeliveryInfo data-id="element-7912" data-mp="qjda5ogg9o6o" />
                </div>
              </div>
            </div>
            {/* Product Tabs */}
            <div className="mt-6 lg:mt-8" data-id="element-7913" data-mp="mijo70mkijgf">
              <ProductTabs product={product} data-id="element-7914" data-mp="mjmf38jw4lwc" />
            </div>
            {/* Customers Also Viewed */}
            <div className="mt-6 lg:mt-8" data-id="element-7915" data-mp="hkrwo7pu4zcr">
              <CustomersAlsoViewed data-id="element-7916" data-mp="1e8xxejjmz7lr" />
            </div>
            {/* Related Products */}
            <div className="mt-6 lg:mt-8 mb-6 lg:mb-0" data-id="element-7917" data-mp="1a6kus406rpt7">
              <RelatedProducts data-id="element-7918" data-mp="1a978kldo71jv" />
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-7919" data-mp="15etb8dfg6kck">
          <Footer data-id="element-7920" data-mp="10hjcg7664wc2" />
        </div>
        <MobileFooterNav data-id="element-7921" data-mp="10g8bef43y2on" />
        <PharmacistChat data-id="element-7922" data-mp="10h180n9crbt2" />
        {/* Sticky Bottom Actions - Mobile Only */}
        <StickyBottomActions product={product} data-id="element-7923" data-mp="1xxgs1gnevd7u" />
        {/* Back to top button */}
        <button className="fixed bottom-44 md:bottom-6 right-4 md:right-6 bg-[#1E2B55] text-white p-3 rounded-full shadow-lg hover:bg-[#162041] transition-colors z-40" onClick={() => window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })} data-id="element-7924" data-mp="1t5ovmphkqd9s">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-7925" data-mp="2adpwyqv54w9r">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" data-id="element-7926" data-mp="2jffxqnawx7k7" />
          </svg>
        </button>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7927" data-mp="2t84vkfdjimx2" />
      </div>
    </HelmetProvider>;
}